import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Wallet, Download, Printer, AlertTriangle, Share2,
  TrendingUp, TrendingDown, ShieldCheck, Building2, FileText, LineChart, FileDown,
} from 'lucide-react';
import PdfModeModal, { getSavedPdfMode } from '@/components/PdfModeModal';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import UnappliedSplitsBanner from '@/components/UnappliedSplitsBanner';
import { ACCOUNT_TYPES, ACCOUNT_TYPE_TONE, getAccountType } from '@/lib/accountTypes';
import { getBroker } from '@/lib/brokers';
import { useActivePortfolioId } from '@/components/PortfolioSelector';
import AbbrTip from '@/components/AbbrTip';
import { toast } from 'sonner';
import { exportNodeAsPdf } from '@/lib/pdfExport';
import { downloadCsv } from '@/lib/csvExport';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tax Account Audit — Round 92o++++++++h.
//
// Dedicated full page (linked from Dashboard + Year-End Report + sitemap)
// that aggregates the user's portfolio by `account_type` so they can
// audit composition, performance, realised gains, and dividend income
// per Taxable / Roth / Trad IRA / 401(k) / HSA / Other bucket. Plays
// nicely with the existing NIIT story — taxable + other roll up into
// the "NIIT-applicable" summary; the rest is "NIIT-exempt".

const fmt = (v, signed = false) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const sign = signed ? (n >= 0 ? '+' : '−') : '';
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmt0 = (v, signed = false) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const sign = signed ? (n >= 0 ? '+' : '−') : '';
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const pct = (v, decimals = 2) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`;
};

// Reuse the same tone palette the Year-End account-mix bar uses so the
// two pages share a visual vocabulary for account_type.
const accountBg = (key) => (ACCOUNT_TYPE_TONE[key] || '').split(' ').find((c) => c.startsWith('bg-')) || 'bg-slate-300';

// Stronger background palette for broker bar segments + legend
// swatches — the 50-shade backgrounds from BROKER_TONE are designed
// for pill chips on light surfaces and disappear in dark mode. These
// 300/700 shades pair 1:1 with BROKER_FILL so the donut, bar, and
// legend swatch all read as the SAME color across themes.
const BROKER_BG_STRONG = {
  schwab:    'bg-blue-300 dark:bg-blue-700',
  fidelity:  'bg-green-300 dark:bg-green-700',
  vanguard:  'bg-red-300 dark:bg-red-700',
  robinhood: 'bg-lime-300 dark:bg-lime-700',
  m1:        'bg-amber-300 dark:bg-amber-700',
  webull:    'bg-orange-300 dark:bg-orange-700',
  ibkr:      'bg-cyan-300 dark:bg-cyan-700',
  etrade:    'bg-violet-300 dark:bg-violet-700',
  merrill:   'bg-purple-300 dark:bg-purple-700',
  sofi:      'bg-teal-300 dark:bg-teal-700',
  public:    'bg-fuchsia-300 dark:bg-fuchsia-700',
  other:     'bg-zinc-300 dark:bg-zinc-600',
  untagged:  'bg-zinc-200 dark:bg-zinc-700',
};
const brokerBg = (key) => BROKER_BG_STRONG[key] || 'bg-zinc-300 dark:bg-zinc-600';

// SVG fill utility classes per broker key — same palette family as
// BROKER_TONE but expressed as `fill-*` so the donut paints correctly.
const BROKER_FILL = {
  schwab:    'fill-blue-300 dark:fill-blue-700',
  fidelity:  'fill-green-300 dark:fill-green-700',
  vanguard:  'fill-red-300 dark:fill-red-700',
  robinhood: 'fill-lime-300 dark:fill-lime-700',
  m1:        'fill-amber-300 dark:fill-amber-700',
  webull:    'fill-orange-300 dark:fill-orange-700',
  ibkr:      'fill-cyan-300 dark:fill-cyan-700',
  etrade:    'fill-violet-300 dark:fill-violet-700',
  merrill:   'fill-purple-300 dark:fill-purple-700',
  sofi:      'fill-teal-300 dark:fill-teal-700',
  public:    'fill-fuchsia-300 dark:fill-fuchsia-700',
  other:     'fill-zinc-300 dark:fill-zinc-600',
  untagged:  'fill-zinc-200 dark:fill-zinc-700',
};
const brokerFill = (key) => BROKER_FILL[key] || 'fill-zinc-300 dark:fill-zinc-600';
const brokerLabel = (key) => {
  if (!key || key === 'untagged') return 'Untagged';
  return getBroker(key)?.label || key;
};
const brokerShort = (key) => {
  if (!key || key === 'untagged') return 'Untagged';
  return getBroker(key)?.short || key;
};

// SVG fill utility classes per account type. Mirrors the bg palette
// the legend uses so the donut slice and the legend swatch read as
// the same color. Tailwind's `fill-*` utilities work directly on
// SVG <path>/<rect>; bg-* utilities don't (they target CSS
// background, which has no effect on SVG paint).
const ACCOUNT_FILL = {
  taxable:         'fill-slate-300 dark:fill-slate-600',
  roth_ira:        'fill-emerald-300 dark:fill-emerald-700',
  traditional_ira: 'fill-amber-300 dark:fill-amber-700',
  '401k':          'fill-violet-300 dark:fill-violet-700',
  hsa:             'fill-rose-300 dark:fill-rose-700',
  other:           'fill-zinc-300 dark:fill-zinc-600',
};
const accountFill = (key) => ACCOUNT_FILL[key] || 'fill-slate-300 dark:fill-slate-600';

function StackedAllocationBar({ rows, total, metric = 'market_value' }) {
  // Single horizontal bar split across present account types, scaled
  // by the chosen metric (market_value or cost_basis). Renders nothing
  // when there's effectively no data, so the page doesn't show an
  // empty stripe for cold-start users.
  if (!rows?.length || !(total > 0)) return null;
  return (
    <div className="flex w-full h-3 rounded-full overflow-hidden ring-1 ring-border" data-testid="ta-alloc-bar">
      {rows.map((r) => {
        const v = Number(r[metric] || 0);
        const p = (v / total) * 100;
        if (p <= 0) return null;
        const at = getAccountType(r.account_type);
        return (
          <div
            key={r.account_type}
            className={accountBg(r.account_type)}
            style={{ width: `${p}%` }}
            title={`${at.label}: ${fmt(v)} (${p.toFixed(1)}%)`}
            data-testid={`ta-alloc-bar-${r.account_type}`}
          />
        );
      })}
    </div>
  );
}

function Donut({ rows, total, metric = 'market_value' }) {
  // 200×200 SVG donut. Conservative defaults — no animation, no
  // labels overlapping the slices; the per-bucket cards below carry
  // the % labels.
  if (!rows?.length || !(total > 0)) return null;
  const size = 200;
  const r = 80;
  const cx = size / 2;
  const cy = size / 2;
  const stroke = 28;
  // Single-bucket portfolios: an arc spanning the full circle has
  // start == end and collapses to a zero-area path. Render a plain
  // <circle> instead so the donut still reads as "100% Roth IRA" etc.
  const activeRows = rows.filter((r0) => Number(r0[metric] || 0) > 0);
  const singleSlice = activeRows.length === 1;
  let acc = 0;
  const arcs = activeRows.map((row) => {
    const v = Number(row[metric] || 0);
    const frac = total > 0 ? v / total : 0;
    const startA = acc * Math.PI * 2 - Math.PI / 2;
    acc += frac;
    const endA = acc * Math.PI * 2 - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(startA);
    const y1 = cy + r * Math.sin(startA);
    const x2 = cx + r * Math.cos(endA);
    const y2 = cy + r * Math.sin(endA);
    return {
      key: row.account_type,
      d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      fillClass: accountFill(row.account_type),
      pct: (frac * 100).toFixed(1),
      label: getAccountType(row.account_type).label,
      value: v,
    };
  });
  return (
    <div className="flex flex-col items-center gap-2" data-testid="ta-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
        <title>Allocation by account type</title>
        {singleSlice ? (
          <circle cx={cx} cy={cy} r={r} className={accountFill(activeRows[0].account_type)}>
            <title>{`${getAccountType(activeRows[0].account_type).label}: ${fmt(activeRows[0][metric])} (100%)`}</title>
          </circle>
        ) : (
          arcs.map((a) => (
            <path key={a.key} d={a.d} className={a.fillClass}>
              <title>{`${a.label}: ${fmt(a.value)} (${a.pct}%)`}</title>
            </path>
          ))
        )}
        {/* Inner hole — clean cut, matches the page bg. */}
        <circle cx={cx} cy={cy} r={r - stroke} className="fill-background" />
        <text x={cx} y={cy - 4} textAnchor="middle" className="fill-foreground text-[11px] font-semibold">
          Total
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" className="fill-foreground text-sm font-mono font-bold">
          {fmt0(total)}
        </text>
      </svg>
    </div>
  );
}

function StatCard({ label, value, sub, tone, testId, icon: Icon }) {
  const tones = {
    emerald: 'border-emerald-300/50 bg-emerald-50/40 dark:bg-emerald-950/20',
    rose:    'border-rose-300/50 bg-rose-50/40 dark:bg-rose-950/20',
    amber:   'border-amber-300/50 bg-amber-50/40 dark:bg-amber-950/20',
    sky:     'border-sky-300/50 bg-sky-50/40 dark:bg-sky-950/20',
    violet:  'border-violet-300/50 bg-violet-50/40 dark:bg-violet-950/20',
    none:    'border-border bg-card',
  };
  return (
    <div className={`rounded-xl border p-3 print-clean ${tones[tone || 'none']}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />}
        {label}
      </div>
      <div className="font-mono text-lg font-bold mt-0.5 break-all">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function AccountCard({ row, year }) {
  const at = getAccountType(row.account_type);
  const Icon = at.icon;
  const ugl = Number(row.unrealized_gain_loss || 0);
  const uglTone = ugl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400';
  const rgl = Number(row.ytd_realised_gain_loss || 0);
  const rglTone = rgl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400';
  const tone = ACCOUNT_TYPE_TONE[row.account_type] || '';
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden print-clean" data-testid={`ta-account-${row.account_type}`}>
      <div className={`flex items-center justify-between gap-2 px-3 py-2 ring-1 ${tone}`}>
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <div className="font-bold text-sm truncate">{at.label}</div>
            <div className="text-[10px] opacity-80">
              {row.position_count} {row.position_count === 1 ? 'position' : 'positions'} · {row.lot_count} {row.lot_count === 1 ? 'lot' : 'lots'} · {Number(row.value_pct_of_total || 0).toFixed(1)}% of portfolio
            </div>
          </div>
        </div>
        {row.niit_applies ? (
          <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200" title="Dividends and gains in this account contribute to NIIT exposure if MAGI is over threshold.">
            NIIT-APPLIES
          </span>
        ) : (
          <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" title="Dividends and gains in this account are exempt from NIIT.">
            NIIT-EXEMPT
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Market Value</div>
            <div className="font-mono font-bold text-base">{fmt(row.market_value)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Cost Basis</div>
            <div className="font-mono font-bold text-base">{fmt(row.cost_basis)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Unrealized G/L</div>
            <div className={`font-mono font-bold text-base ${uglTone}`}>
              {fmt(row.unrealized_gain_loss, true)}
              {row.unrealized_gain_loss_pct != null && (
                <span className="text-[10px] font-normal opacity-80 ml-1">({pct(row.unrealized_gain_loss_pct)})</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Realised {year} YTD</div>
            <div className={`font-mono font-bold text-base ${rglTone}`}>{fmt(row.ytd_realised_gain_loss, true)}</div>
            <div className="text-[9px] text-muted-foreground">lifetime {fmt(row.lifetime_realised_gain_loss, true)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Dividends {year} YTD</div>
            <div className="font-mono font-bold text-base">{fmt(row.ytd_dividends_collected)}</div>
            <div className="text-[9px] text-muted-foreground">lifetime {fmt(row.lifetime_dividends_collected)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Forward Annual Income</div>
            <div className="font-mono font-bold text-base">{fmt(row.forward_annual_income)}</div>
            <div className="text-[9px] text-muted-foreground">est. fwd 12 mo.</div>
          </div>
        </div>
        {row.top_holdings?.length > 0 && (
          <div className="mt-3 pt-2 border-t border-border/60">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Top Holdings</div>
            <ul className="space-y-0.5">
              {row.top_holdings.map((h) => (
                <li key={h.ticker} className="flex items-center justify-between text-[11px]" data-testid={`ta-holding-${row.account_type}-${h.ticker}`}>
                  <span className="font-mono font-bold truncate">{h.ticker}</span>
                  <span className="font-mono text-muted-foreground tabular-nums">{fmt(h.market_value)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// Broker dividend forecast — same broker buckets but sliced on
// forward annual dividend income. Lets users see "how much annual
// income is coming from each brokerage" so they can decide whether
// to consolidate / move fund families. Hidden when fewer than 2
// buckets or when nobody has forward income (e.g. a growth-only
// portfolio).
function BrokerDividendForecastCard({ rows }) {
  if (!rows || rows.length < 2) return null;
  const incomeRows = rows
    .map((r) => ({ broker: r.broker, income: Number(r.forward_annual_income || 0), lots: r.lot_count }))
    .filter((r) => r.income > 0);
  if (incomeRows.length < 2) return null;
  const total = incomeRows.reduce((s, r) => s + r.income, 0);
  if (total <= 0) return null;
  const size = 200;
  const r = 80;
  const cx = size / 2;
  const cy = size / 2;
  const stroke = 28;
  let acc = 0;
  const arcs = incomeRows.map((row) => {
    const frac = row.income / total;
    const startA = acc * Math.PI * 2 - Math.PI / 2;
    acc += frac;
    const endA = acc * Math.PI * 2 - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(startA);
    const y1 = cy + r * Math.sin(startA);
    const x2 = cx + r * Math.cos(endA);
    const y2 = cy + r * Math.sin(endA);
    return {
      key: row.broker,
      d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      fillClass: brokerFill(row.broker),
      pct: (frac * 100).toFixed(1),
      label: brokerLabel(row.broker),
      income: row.income,
      lots: row.lots,
    };
  });
  return (
    <div className="rounded-xl border bg-card p-4 mb-6 print-clean" data-testid="ta-broker-income-card">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-bold tracking-tight flex items-center gap-1.5">
          <Wallet className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          Dividend Forecast by Brokerage
        </h2>
        <span className="text-[10px] text-muted-foreground">forward annual income</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 items-center">
        <div className="flex flex-col items-center gap-2" data-testid="ta-broker-income-donut">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
            <title>Forward dividend income by broker</title>
            {arcs.length === 1 ? (
              <circle cx={cx} cy={cy} r={r} className={brokerFill(arcs[0].key)}>
                <title>{`${arcs[0].label}: ${fmt(arcs[0].income)}/yr (100%)`}</title>
              </circle>
            ) : (
              arcs.map((a) => (
                <path key={a.key} d={a.d} className={a.fillClass}>
                  <title>{`${a.label}: ${fmt(a.income)}/yr (${a.pct}%)`}</title>
                </path>
              ))
            )}
            <circle cx={cx} cy={cy} r={r - stroke} className="fill-background" />
            <text x={cx} y={cy - 4} textAnchor="middle" className="fill-foreground text-[11px] font-semibold">Per year</text>
            <text x={cx} y={cy + 14} textAnchor="middle" className="fill-foreground text-sm font-mono font-bold">{fmt0(total)}</text>
          </svg>
        </div>
        <div className="space-y-3">
          <div className="flex w-full h-3 rounded-full overflow-hidden ring-1 ring-border" data-testid="ta-broker-income-bar">
            {incomeRows.map((row) => {
              const p = (row.income / total) * 100;
              if (p <= 0) return null;
              return (
                <div
                  key={row.broker}
                  className={brokerBg(row.broker)}
                  style={{ width: `${p}%` }}
                  title={`${brokerLabel(row.broker)}: ${fmt(row.income)}/yr (${p.toFixed(1)}%)`}
                  data-testid={`ta-broker-income-bar-${row.broker}`}
                />
              );
            })}
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {incomeRows.map((row) => {
              const p = (row.income / total) * 100;
              const monthly = row.income / 12;
              return (
                <li
                  key={row.broker}
                  className="flex items-center gap-2 text-xs rounded-md border border-border/60 px-2 py-1.5"
                  data-testid={`ta-broker-income-legend-${row.broker}`}
                >
                  <span className={`w-3 h-3 rounded ${brokerBg(row.broker)} ring-1 ring-border/40 shrink-0`} aria-hidden="true" />
                  <div className="flex-1 min-w-0 leading-tight">
                    <div className="font-semibold truncate">{brokerShort(row.broker)}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {fmt(monthly)}/mo · {row.lots} lot{row.lots === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono tabular-nums font-bold">{fmt(row.income)}</div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">{p.toFixed(1)}%</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-3 leading-snug">
        Forward income = current shares &times; the latest known annual dividend rate per ticker, summed per broker.
        Helps decide whether to consolidate brokerages, move fund families, or relocate income-heavy lots into a tax-advantaged account.
      </p>
    </div>
  );
}

// Broker rollup card — parallels the Allocation by Account Type card,
function BrokerRollupCard({ rows, total }) {
  if (!rows || rows.length < 2) return null;
  const size = 200;
  const r = 80;
  const cx = size / 2;
  const cy = size / 2;
  const stroke = 28;
  const activeRows = rows.filter((r0) => Number(r0.market_value || 0) > 0);
  if (!activeRows.length) return null;
  const singleSlice = activeRows.length === 1;
  let acc = 0;
  const arcs = activeRows.map((row) => {
    const v = Number(row.market_value || 0);
    const frac = total > 0 ? v / total : 0;
    const startA = acc * Math.PI * 2 - Math.PI / 2;
    acc += frac;
    const endA = acc * Math.PI * 2 - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(startA);
    const y1 = cy + r * Math.sin(startA);
    const x2 = cx + r * Math.cos(endA);
    const y2 = cy + r * Math.sin(endA);
    return {
      key: row.broker,
      d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      fillClass: brokerFill(row.broker),
      pct: (frac * 100).toFixed(1),
      label: brokerLabel(row.broker),
      value: v,
    };
  });
  return (
    <div className="rounded-xl border bg-card p-4 mb-6 print-clean" data-testid="ta-broker-card">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-bold tracking-tight flex items-center gap-1.5">
          <Building2 className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          Rollup by Brokerage
        </h2>
        <span className="text-[10px] text-muted-foreground">% of market value</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 items-center">
        <div className="flex flex-col items-center gap-2" data-testid="ta-broker-donut">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
            <title>Allocation by broker</title>
            {singleSlice ? (
              <circle cx={cx} cy={cy} r={r} className={brokerFill(activeRows[0].broker)}>
                <title>{`${brokerLabel(activeRows[0].broker)}: ${fmt(activeRows[0].market_value)} (100%)`}</title>
              </circle>
            ) : (
              arcs.map((a) => (
                <path key={a.key} d={a.d} className={a.fillClass}>
                  <title>{`${a.label}: ${fmt(a.value)} (${a.pct}%)`}</title>
                </path>
              ))
            )}
            <circle cx={cx} cy={cy} r={r - stroke} className="fill-background" />
            <text x={cx} y={cy - 4} textAnchor="middle" className="fill-foreground text-[11px] font-semibold">Total</text>
            <text x={cx} y={cy + 14} textAnchor="middle" className="fill-foreground text-sm font-mono font-bold">{fmt0(total)}</text>
          </svg>
        </div>
        <div className="space-y-3">
          <div className="flex w-full h-3 rounded-full overflow-hidden ring-1 ring-border" data-testid="ta-broker-bar">
            {activeRows.map((r0) => {
              const v = Number(r0.market_value || 0);
              const p = total > 0 ? (v / total) * 100 : 0;
              if (p <= 0) return null;
              return (
                <div
                  key={r0.broker}
                  className={brokerBg(r0.broker)}
                  style={{ width: `${p}%` }}
                  title={`${brokerLabel(r0.broker)}: ${fmt(v)} (${p.toFixed(1)}%)`}
                  data-testid={`ta-broker-bar-${r0.broker}`}
                />
              );
            })}
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {activeRows.map((r0) => {
              const v = Number(r0.market_value || 0);
              const p = total > 0 ? (v / total) * 100 : 0;
              return (
                <li
                  key={r0.broker}
                  className="flex items-center gap-2 text-xs rounded-md border border-border/60 px-2 py-1.5"
                  data-testid={`ta-broker-legend-${r0.broker}`}
                >
                  <span className={`w-3 h-3 rounded ${brokerBg(r0.broker)} ring-1 ring-border/40`} aria-hidden="true" />
                  <span className="font-semibold truncate flex-1 min-w-0">{brokerShort(r0.broker)}</span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">{r0.lot_count} lot{r0.lot_count === 1 ? '' : 's'}</span>
                  <span className="font-mono tabular-nums font-bold">{p.toFixed(1)}%</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-3 leading-snug">
        Tag each lot&rsquo;s brokerage from the Portfolio page (click the broker pill next to any position). Lots without a broker tag roll up as <strong>Untagged</strong>.
      </p>
    </div>
  );
}

// Multi-year compare — fetches /tax-audit/compare?years=Y,Y-1,Y-2 and
// renders a side-by-side mini-summary so users can see how their tax
// picture evolved across recent years. Hidden behind a toggle to keep
// the default audit view lean.
function CompareSection({ baseYear }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [years, setYears] = useState([]);
  const [byYear, setByYear] = useState({});

  // Round P108r (Feb 2026) — scope audit to active portfolio.
  const { activeId: activePortfolioId } = useActivePortfolioId();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const wanted = [baseYear, baseYear - 1, baseYear - 2];
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tax-audit/compare`, {
          params: activePortfolioId ? { years: wanted.join(','), portfolio_id: activePortfolioId } : { years: wanted.join(',') },
          withCredentials: true,
        });
        if (cancelled) return;
        const yrs = data?.years || [];
        const map = {};
        (data?.summaries || []).forEach((y) => { map[y.year] = y; });
        setYears(yrs.sort((a, b) => b - a)); // newest first
        setByYear(map);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, baseYear]);

  return (
    <div className="rounded-xl border bg-card p-4 mb-6 print-clean" data-testid="ta-compare-card">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-bold tracking-tight flex items-center gap-1.5">
          <LineChart className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          Multi-Year Compare
        </h2>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border bg-card hover:bg-muted text-xs font-semibold transition-colors"
          data-testid="ta-compare-toggle"
        >
          {open ? 'Hide compare' : `Compare ${baseYear}, ${baseYear - 1}, ${baseYear - 2}`}
        </button>
      </div>
      {open && loading && (
        <div className="text-xs text-muted-foreground py-3" data-testid="ta-compare-loading">Loading {years.length || 3}-year compare…</div>
      )}
      {open && error && (
        <div className="text-xs text-rose-600 dark:text-rose-400 py-2" data-testid="ta-compare-error">
          Couldn&apos;t load compare: {error}
        </div>
      )}
      {open && !loading && !error && years.length > 0 && (
        <div className="overflow-x-auto" data-testid="ta-compare-table-wrap">
          <table className="w-full text-xs" data-testid="ta-compare-table">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">Metric</th>
                {years.map((y) => (
                  <th key={y} className="text-right py-2 px-2 font-mono font-bold" data-testid={`ta-compare-yr-${y}`}>{y}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <CompareRow label="Market Value" pick={(t) => t.market_value} fmt={fmt} years={years} byYear={byYear} testId="value" />
              <CompareRow label="Cost Basis" pick={(t) => t.cost_basis} fmt={fmt} years={years} byYear={byYear} testId="cost" />
              <CompareRow label="Unrealized G/L" pick={(t) => t.unrealized_gain_loss} fmt={(v) => fmt(v, true)} years={years} byYear={byYear} tonePos="emerald" toneNeg="rose" testId="ugl" />
              <CompareRow label="Realised YTD" pick={(t) => t.ytd_realised_gain_loss} fmt={(v) => fmt(v, true)} years={years} byYear={byYear} tonePos="emerald" toneNeg="rose" testId="realised" />
              <CompareRow label="Dividends YTD" pick={(t) => t.ytd_dividends_collected} fmt={fmt} years={years} byYear={byYear} testId="div" />
              <CompareRow label="Forward Annual Income" pick={(t) => t.forward_annual_income} fmt={fmt} years={years} byYear={byYear} testId="fwd" />
              <CompareRow label="Positions" pick={(t) => t.position_count} fmt={(v) => (v == null ? '—' : String(v))} years={years} byYear={byYear} testId="positions" />
              <CompareRow label="Lots" pick={(t) => t.lot_count} fmt={(v) => (v == null ? '—' : String(v))} years={years} byYear={byYear} testId="lots" />
            </tbody>
          </table>
          <p className="text-[10px] text-muted-foreground mt-2 leading-snug">
            Realised + dividend YTD totals are scoped to each respective tax year. Market value, cost basis, unrealized G/L, position counts and forward income reflect today&rsquo;s portfolio (point-in-time snapshots aren&rsquo;t reconstructable from current data).
          </p>
        </div>
      )}
    </div>
  );
}

function CompareRow({ label, pick, fmt: fmtFn, years, byYear, tonePos, toneNeg, testId }) {
  // Note: Tailwind JIT cannot resolve dynamic class names from runtime
  // strings — every utility used here must be present as a literal
  // somewhere in source. We only use emerald/rose for the two
  // gain/loss rows, so list them explicitly.
  const POS_CLS = { emerald: 'text-emerald-600 dark:text-emerald-400' };
  const NEG_CLS = { rose: 'text-rose-600 dark:text-rose-400' };
  return (
    <tr className="border-b border-border/40" data-testid={`ta-compare-row-${testId}`}>
      <td className="py-1.5 px-2 text-muted-foreground">{label}</td>
      {years.map((y) => {
        const totals = byYear?.[y]?.totals || {};
        const raw = pick(totals);
        const n = Number(raw);
        let cls = 'font-mono tabular-nums';
        if (tonePos && Number.isFinite(n)) {
          cls += ' ' + (n >= 0 ? (POS_CLS[tonePos] || '') : (NEG_CLS[toneNeg] || ''));
        }
        return (
          <td key={y} className={`text-right py-1.5 px-2 ${cls}`} data-testid={`ta-compare-cell-${testId}-${y}`}>
            {fmtFn(raw)}
          </td>
        );
      })}
    </tr>
  );
}

export default function TaxAccountAudit() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialYear = Number(searchParams.get('year')) || new Date().getFullYear();
  const [year, setYear] = useState(initialYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { activeId: activePortfolioId } = useActivePortfolioId();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tax-audit/summary`, {
          params: activePortfolioId ? { year, portfolio_id: activePortfolioId } : { year },
          withCredentials: true,
        });
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          if (status === 401) {
            setError('Sign in to view your Tax Account Audit.');
          } else {
            setError(err?.response?.data?.detail || err?.message || 'Failed to load');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year, activePortfolioId]);

  // Keep ?year=… in sync so the page is truly bookmark-friendly per
  // the user's "save this URL so they can check back" requirement.
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    sp.set('year', String(year));
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const totals = data?.totals || {};
  const byAccount = useMemo(() => data?.by_account || [], [data]);
  const byBroker = useMemo(() => data?.by_broker || [], [data]);
  const niit = data?.niit_summary || {};
  const yearOptions = data?.available_years || [year];

  const handleCsv = () => {
    if (!byAccount.length) return;
    const headerLine = [
      'Account Type', 'NIIT Applies', 'Position Count', 'Lot Count', 'Shares',
      'Cost Basis', 'Market Value', 'Unrealized G/L', 'Unrealized G/L %',
      `Realised ${year} YTD`, 'Lifetime Realised',
      `Dividends ${year} YTD`, 'Lifetime Dividends',
      'Forward Annual Income', '% of Portfolio Value',
    ];
    const dataLines = byAccount.map((r) => [
      r.label, r.niit_applies ? 'Yes' : 'No', r.position_count, r.lot_count, r.share_count,
      r.cost_basis, r.market_value, r.unrealized_gain_loss, r.unrealized_gain_loss_pct,
      r.ytd_realised_gain_loss, r.lifetime_realised_gain_loss,
      r.ytd_dividends_collected, r.lifetime_dividends_collected,
      r.forward_annual_income, r.value_pct_of_total,
    ]);
    const totalLine = [
      'TOTAL', '', totals.position_count, totals.lot_count, totals.share_count,
      totals.cost_basis, totals.market_value, totals.unrealized_gain_loss, totals.unrealized_gain_loss_pct,
      totals.ytd_realised_gain_loss, totals.lifetime_realised_gain_loss,
      totals.ytd_dividends_collected, totals.lifetime_dividends_collected,
      totals.forward_annual_income, '',
    ];
    const niitLines = [
      [],
      ['NIIT-Applicable Slice (Taxable + Other)'],
      ['Market Value',           niit.applicable?.market_value],
      ['Cost Basis',              niit.applicable?.cost_basis],
      ['Unrealized G/L',          niit.applicable?.unrealized_gain_loss],
      [`Realised ${year} YTD`,    niit.applicable?.ytd_realised_gain_loss],
      [`Dividends ${year} YTD`,   niit.applicable?.ytd_dividends_collected],
      ['Forward Annual Income',   niit.applicable?.forward_annual_income],
      [],
      ['NIIT-Exempt Slice (Roth + Trad IRA + 401(k) + HSA)'],
      ['Market Value',            niit.exempt?.market_value],
      ['Forward Annual Income',   niit.exempt?.forward_annual_income],
    ];
    // Per-holding detail block per the user's "give me a smaller version
    // of the full-portfolio export" request — useful inside Excel.
    const holdingLines = [
      [],
      ['Top Holdings by Account Type'],
      ['Account', 'Ticker', 'Name', 'Shares', 'Cost Basis', 'Market Value', 'Annual Income'],
    ];
    byAccount.forEach((r) => {
      (r.top_holdings || []).forEach((h) => {
        holdingLines.push([r.label, h.ticker, h.name, h.shares, h.cost_basis, h.market_value, h.annual_income]);
      });
    });
    const filename = `dividrip-tax-account-audit-${year}.csv`;
    try {
      downloadCsv(filename, [
        headerLine, ...dataLines, totalLine, ...niitLines, ...holdingLines,
      ]);
      // Surface explicit feedback — some browsers route the file directly
      // to a download folder with no notification, so without this toast
      // the user has no signal that the click did anything.
      toast.success(`Saved ${filename}`);
    } catch (e) {
      toast.error('CSV export failed — try again or use Print instead.');
    }
  };

  const handleShare = async () => {
    // Round 92o++++++++h-fix1 — pure clipboard flow. Earlier rev tried
    // `navigator.share` first; on Chromium desktop the OS share sheet
    // opens and cancelling it threw an AbortError that our catch ate,
    // making the button feel broken. "Save Link" is a bookmark intent,
    // so just copy to clipboard and confirm via toast.
    const url = `${window.location.origin}/tax-account-audit?year=${year}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied — paste it anywhere to come back here later.');
        return;
      }
    } catch (e) {
      // Fall through to the execCommand path below — clipboard API can
      // reject when the page isn't focused or the permissions API is
      // locked down (corporate browsers, embedded WebViews, etc.).
    }
    // Legacy fallback: works in older Safari + restricted contexts.
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        toast.success('Link copied — paste it anywhere to come back here later.');
      } else {
        throw new Error('execCommand returned false');
      }
    } catch (e) {
      // Last resort — surface the URL so the user can copy manually.
      toast.error(`Couldn't copy automatically. URL: ${url}`);
    }
  };

  const handlePrint = () => {
    // window.print() MUST be invoked synchronously inside the user-
    // gesture click handler. Wrapping it in setTimeout(...) breaks the
    // gesture chain — Chrome 100+ silently blocks the print dialog in
    // that case. The toast is a fire-and-forget side effect.
    try {
      toast.message('Opening print dialog…');
    } catch (_e) {
      // Toast subsystem hiccup shouldn't prevent printing.
    }
    try {
      window.print();
    } catch (e) {
      toast.error('Print blocked by the browser. Try the Excel/CSV export instead.');
    }
  };

  // Round 92o++++++++N+ — explicit "Save as PDF" button (separate
  // from Print, which the user wants left alone). Uses html2pdf.js
  // (dynamic-imported so the ~200KB lib only loads when the user
  // clicks the button — no impact on initial page load). Renders the
  // entire audit content area to a multi-page A4 PDF on a clean white
  // background so the export looks correct regardless of the user's
  // active light/dark theme.
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  // Round 92o++++++++N+++ — handlePdf now accepts a `mode` from the
  // PdfModeModal: 'download' (save to disk), 'newtab' (preview only),
  // or 'both'. Round 92o++++++++N++++++++ delegates to the shared
  // `exportNodeAsPdf` helper so the layout-hardening fixes (badge
  // nowrap, card page-break-avoid, grid stack) apply everywhere.
  const handlePdf = async (mode = 'download') => {
    setPdfBusy(true);
    try {
      await exportNodeAsPdf({
        elementId: 'ta-printable',
        filename: `dividrip-tax-account-audit-${year}.pdf`,
        mode,
        headerSelector: '.ta-pdf-header',
        user,
        onAfter: () => {
          const hasSavedPref = !!getSavedPdfMode();
          if (hasSavedPref) {
            // Re-issue the success toast with a "Change…" action so
            // the user can pop the mode picker if they want to
            // tweak their default.
            toast.success('Tip: tap "Change…" anytime to switch your default mode.', {
              action: { label: 'Change…', onClick: () => setPdfModalOpen(true) },
              duration: 4000,
            });
          }
        },
      });
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100svh] bg-background text-foreground">
      <style>{`
        /* Round 92o++++++++h+ (Feb 2026) — unified PRINT + PDF layout &
           theme normalization for the Tax Account Audit.

           Print already worked (site header hidden, 5-tile row wraps
           naturally to two rows on paper). This block adds the same
           behaviour to the PDF export path:
             1. Force the top 5 tiles into a 3+2 layout on PDF (was
                cramming all 5 onto one line and truncating badges).
             2. Force LIGHT theme on the report content regardless of
                the app's current dark/light mode — dark-mode PDF
                looked washed out and hard to read at the top. */

        /* --- Print rules (window.print) --------------------------- */
        @media print {
          /* Hide the global app header (logo bar + How To / Glossary /
             Learn nav row) so the printed page is just the audit
             content. The <header> element is rendered by AppHeader.js
             — targeting the tag keeps this scoped without needing a
             classname pact across components. */
          header,
          [data-sonner-toaster],
          [data-sonner-toaster] * {
            display: none !important;
          }
          .no-print { display: none !important; }
          body { background: white !important; color: black !important; }
          .print-clean { background: white !important; color: black !important; box-shadow: none !important; border-color: #ccc !important; }
          a { color: black !important; text-decoration: none !important; }
          #ta-printable,
          #ta-printable * {
            color: black !important;
            background-color: transparent !important;
            border-color: #ccc !important;
          }
          #ta-printable .print-clean,
          #ta-printable [data-pdf-keep] {
            background-color: #ffffff !important;
          }
        }

        /* --- PDF rules (html2pdf.js via body.pdf-rendering) ------- */
        body.pdf-rendering .no-print { display: none !important; }

        /* 5 tiles → 3 across on top row, remaining 2 wrap below. */
        body.pdf-rendering #ta-printable .grid.lg\\:grid-cols-5 {
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        }

        /* Force light theme inside the PDF snapshot. html2canvas
           captures whatever the DOM is at render time — we override
           to a legible white background + dark text for every child.
           Semantic tint colors (gain green, loss red, brand tints)
           are preserved by the per-utility overrides below. */
        body.pdf-rendering #ta-printable,
        body.pdf-rendering #ta-printable * {
          color: #0f172a !important;
          border-color: #d1d5db !important;
        }
        body.pdf-rendering #ta-printable,
        body.pdf-rendering #ta-printable .bg-background,
        body.pdf-rendering #ta-printable .bg-card,
        body.pdf-rendering #ta-printable .bg-muted,
        body.pdf-rendering #ta-printable .bg-muted\\/30,
        body.pdf-rendering #ta-printable .bg-muted\\/40,
        body.pdf-rendering #ta-printable .bg-muted\\/50,
        body.pdf-rendering #ta-printable .print-clean,
        body.pdf-rendering #ta-printable [data-pdf-keep] {
          background-color: #ffffff !important;
        }
        /* Preserve semantic informational tints. */
        body.pdf-rendering #ta-printable .text-emerald-600,
        body.pdf-rendering #ta-printable .text-emerald-500,
        body.pdf-rendering #ta-printable .text-emerald-700 { color: #059669 !important; }
        body.pdf-rendering #ta-printable .text-red-600,
        body.pdf-rendering #ta-printable .text-red-500,
        body.pdf-rendering #ta-printable .text-red-700 { color: #dc2626 !important; }
        body.pdf-rendering #ta-printable .text-cyan-600,
        body.pdf-rendering #ta-printable .text-cyan-500,
        body.pdf-rendering #ta-printable .text-cyan-700 { color: #0891b2 !important; }
        body.pdf-rendering #ta-printable .text-amber-600,
        body.pdf-rendering #ta-printable .text-amber-500,
        body.pdf-rendering #ta-printable .text-amber-700 { color: #d97706 !important; }
        body.pdf-rendering #ta-printable .text-violet-600,
        body.pdf-rendering #ta-printable .text-violet-500,
        body.pdf-rendering #ta-printable .text-violet-700 { color: #7c3aed !important; }
        body.pdf-rendering #ta-printable .text-rose-600,
        body.pdf-rendering #ta-printable .text-rose-500,
        body.pdf-rendering #ta-printable .text-rose-700 { color: #e11d48 !important; }
        body.pdf-rendering #ta-printable .text-muted-foreground { color: #64748b !important; }
      `}</style>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6 gap-2 no-print flex-wrap">
          <div className="flex items-center gap-3">
            <BackOrHomeButton testid="ta-back-or-home-btn" showEscHint={false} />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <Wallet className="w-7 h-7 text-violet-600 dark:text-violet-400" />
                Tax Account Audit
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Composition, performance, and dividend income broken out per Taxable / Roth / Trad IRA / 401(k) / HSA / Other.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 px-2 rounded-lg border bg-card text-sm font-mono font-bold"
              data-testid="ta-year-select"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-card hover:bg-muted text-sm font-semibold transition-colors"
              data-testid="ta-share-btn"
              title="Copy a bookmark link for this audit"
            >
              <Share2 className="w-3.5 h-3.5" />
              Save Link
            </button>
            <button
              type="button"
              onClick={handleCsv}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-card hover:bg-muted text-sm font-semibold transition-colors disabled:opacity-50"
              disabled={!byAccount.length}
              data-testid="ta-csv-btn"
              title="Download as CSV — opens in Excel / Google Sheets"
            >
              <Download className="w-3.5 h-3.5" />
              Excel/CSV
            </button>
            <button
              type="button"
              onClick={() => {
                // Power-user shortcut: if the user previously asked us
                // to remember their choice, skip the modal entirely
                // and run the saved mode directly. The success toast
                // ships with a "Change…" action so they can reopen
                // the modal whenever they want.
                const saved = getSavedPdfMode();
                if (saved) {
                  handlePdf(saved);
                } else {
                  setPdfModalOpen(true);
                }
              }}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-card hover:bg-muted text-sm font-semibold transition-colors disabled:opacity-50"
              disabled={!byAccount.length || pdfBusy}
              data-testid="ta-pdf-btn"
              title="Download the PDF or open it in a new tab"
            >
              <FileDown className="w-3.5 h-3.5" />
              {pdfBusy ? 'Building…' : 'Save/View PDF'}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors disabled:opacity-50"
              disabled={!byAccount.length}
              data-testid="ta-print-btn"
              title="Print or save as PDF"
            >
              <Printer className="w-3.5 h-3.5" />
              Print
            </button>
          </div>
        </div>

        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold">Tax Account Audit — {year}</h1>
          <p className="text-xs text-gray-600">
            Generated by DiviDrip (twylightcrow.com) · {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Feb 2026 — Split-not-applied banner. Hidden in the PDF
            capture (print:hidden) since it's an action prompt, not
            report content. */}
        <div className="print:hidden">
          <UnappliedSplitsBanner portfolioId={activePortfolioId} />
        </div>

        {/* id="ta-printable" wraps the body content so html2pdf can
            capture the audit (disclaimer through bottom) without
            including the no-print toolbar above. The print-only h1
            above ALSO sits outside this so it only shows on
            window.print()—the PDF gets the live h1 from the toolbar
            row via a print-only header injected at capture time. */}
        <div id="ta-printable" data-pdf-root>
          {/* Header injected only into PDF capture — html2canvas walks
              the live DOM, so a normally-hidden block becomes visible
              after we toggle a class right before capture. Keeps the
              live page chrome unchanged. */}
          <div className="hidden ta-pdf-header mb-3">
            <h2 className="text-xl font-bold text-black">Tax Account Audit — {year}</h2>
            <p className="text-[11px] text-gray-700">
              Generated by DiviDrip · {new Date().toLocaleDateString()}
            </p>
          </div>
        <div className="rounded-lg border border-amber-300/60 bg-amber-50/50 dark:border-amber-700/50 dark:bg-amber-950/20 p-3 mb-5 text-[11px] text-amber-900 dark:text-amber-200 print-clean" data-testid="ta-disclaimer">
          <strong>Educational record, not tax advice.</strong> Account-type tags are user-applied at the lot level — verify they match the actual brokerage account holding each position. <AbbrTip term="NIIT">NIIT</AbbrTip> and <AbbrTip term="MAGI">MAGI</AbbrTip> figures use IRS thresholds that may change. Realised gain/loss YTD uses sales tagged with the matching tax year; for legacy sells without an account_type stamp, the audit treats them as &ldquo;Taxable&rdquo; (conservative for NIIT). Always reconcile with your 1099-B and 1099-DIV.
        </div>

        {loading && (
          <div className="rounded-lg border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground" data-testid="ta-loading">
            Loading {year} audit…
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-rose-300/60 bg-rose-50/50 dark:bg-rose-950/20 p-4 text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2" data-testid="ta-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <strong>Couldn&apos;t load the audit.</strong>
              <div className="mt-1">{error}</div>
              {error?.toLowerCase?.().includes('sign in') && (
                <div className="mt-2"><Link to="/" className="underline">Go to sign in →</Link></div>
              )}
            </div>
          </div>
        )}
        {!loading && !error && byAccount.length === 0 && (
          <div className="rounded-lg border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground" data-testid="ta-empty">
            No positions or transactions to audit yet for {year}. Add positions (or tag lots with an account type) on the
            {' '}<Link to="/" className="underline">Portfolio</Link> page first.
          </div>
        )}

        {!loading && !error && byAccount.length > 0 && (
          <>
            {/* Headline stat tiles */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              <StatCard
                label="Total Market Value"
                value={fmt(totals.market_value)}
                sub={`${totals.position_count} positions · ${totals.lot_count} lots`}
                tone="sky"
                testId="ta-stat-value"
              />
              <StatCard
                label="Cost Basis"
                value={fmt(totals.cost_basis)}
                tone="none"
                testId="ta-stat-cost"
              />
              <StatCard
                label="Unrealized G/L"
                value={fmt(totals.unrealized_gain_loss, true)}
                sub={totals.unrealized_gain_loss_pct != null ? pct(totals.unrealized_gain_loss_pct) : '—'}
                tone={Number(totals.unrealized_gain_loss || 0) >= 0 ? 'emerald' : 'rose'}
                icon={Number(totals.unrealized_gain_loss || 0) >= 0 ? TrendingUp : TrendingDown}
                testId="ta-stat-ugl"
              />
              <StatCard
                label={`Realised ${year} YTD`}
                value={fmt(totals.ytd_realised_gain_loss, true)}
                sub={`lifetime ${fmt(totals.lifetime_realised_gain_loss, true)}`}
                tone={Number(totals.ytd_realised_gain_loss || 0) >= 0 ? 'emerald' : 'rose'}
                testId="ta-stat-rgl"
              />
              <StatCard
                label={`Dividends ${year} YTD`}
                value={fmt(totals.ytd_dividends_collected)}
                sub={`lifetime ${fmt(totals.lifetime_dividends_collected)} · fwd ${fmt(totals.forward_annual_income)}/yr`}
                tone="violet"
                testId="ta-stat-div"
              />
            </div>

            {/* Allocation visuals */}
            <div className="rounded-xl border bg-card p-4 mb-6 print-clean" data-testid="ta-allocation-card">
              <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
                <h2 className="text-sm font-bold tracking-tight">Allocation by Account Type</h2>
                <span className="text-[10px] text-muted-foreground">% of market value</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 items-center">
                <Donut rows={byAccount} total={totals.market_value} metric="market_value" />
                <div className="space-y-3">
                  <StackedAllocationBar rows={byAccount} total={totals.market_value} metric="market_value" />
                  <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {byAccount.map((r) => {
                      const at = getAccountType(r.account_type);
                      const Icon = at.icon;
                      return (
                        <li key={r.account_type} className="flex items-center gap-2 text-xs" data-testid={`ta-legend-${r.account_type}`}>
                          <span className={`w-3 h-3 rounded ${accountBg(r.account_type)}`} aria-hidden="true" />
                          <Icon className="w-3 h-3 shrink-0 opacity-70" aria-hidden="true" />
                          <span className="font-semibold truncate">{at.short}</span>
                          <span className="ml-auto font-mono tabular-nums">{Number(r.value_pct_of_total || 0).toFixed(1)}%</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>

            {/* Broker rollup — only renders when 2+ broker buckets */}
            <BrokerRollupCard rows={byBroker} total={totals.market_value} />
            <BrokerDividendForecastCard rows={byBroker} />

            {/* NIIT slice summary */}
            <div className="rounded-xl border bg-card p-4 mb-6 print-clean" data-testid="ta-niit-card">
              <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
                <h2 className="text-sm font-bold tracking-tight flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  <AbbrTip term="NIIT">NIIT</AbbrTip> Exposure Split
                </h2>
                <Link to="/year-end-report" className="text-[11px] underline text-muted-foreground hover:text-foreground" data-testid="ta-niit-link">
                  Personalised NIIT calc on Year-End Report →
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/20 p-3" data-testid="ta-niit-applicable">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-200">NIIT-Applicable</h3>
                    <span className="text-[9px] text-amber-700 dark:text-amber-300/80">Taxable + Other</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-[10px] text-muted-foreground">Market Value</div>
                      <div className="font-mono font-bold">{fmt(niit.applicable?.market_value)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">Forward Annual Income</div>
                      <div className="font-mono font-bold">{fmt(niit.applicable?.forward_annual_income)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">Dividends {year} YTD</div>
                      <div className="font-mono font-bold">{fmt(niit.applicable?.ytd_dividends_collected)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">Realised {year} YTD</div>
                      <div className="font-mono font-bold">{fmt(niit.applicable?.ytd_realised_gain_loss, true)}</div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-300/60 bg-emerald-50/40 dark:bg-emerald-950/20 p-3" data-testid="ta-niit-exempt">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-200">NIIT-Exempt</h3>
                    <span className="text-[9px] text-emerald-700 dark:text-emerald-300/80">Roth + Trad IRA + 401(k) + HSA</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-[10px] text-muted-foreground">Market Value</div>
                      <div className="font-mono font-bold">{fmt(niit.exempt?.market_value)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">Forward Annual Income</div>
                      <div className="font-mono font-bold">{fmt(niit.exempt?.forward_annual_income)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">Dividends {year} YTD</div>
                      <div className="font-mono font-bold">{fmt(niit.exempt?.ytd_dividends_collected)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">Realised {year} YTD</div>
                      <div className="font-mono font-bold">{fmt(niit.exempt?.ytd_realised_gain_loss, true)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Per-account cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
              {byAccount.map((r) => (
                <AccountCard key={r.account_type} row={r} year={year} />
              ))}
            </div>

            {/* Multi-year compare — toggled, lives at the bottom */}
            <CompareSection baseYear={year} />
          </>
        )}
        </div>{/* /#ta-printable */}
      </div>
      <PdfModeModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        onApply={(mode) => handlePdf(mode)}
        title="Save / View Tax Account Audit PDF"
      />
    </div>
  );
}
